import { isNil } from "lodash";
import numeral from "numeral";

function getRandomHighContrastColors(count = 30) {
  const colors = [];
  const threshold = 128; // Adjust this value to control minimum contrast

  while (colors.length < count) {
    const red = Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
    const green = Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
    const blue = Math.floor(Math.random() * 256).toString(16).padStart(2, '0');

    // Check for high contrast with white and black backgrounds
    const lum1 = 0.2126 * parseInt(red, 16) + 0.7152 * parseInt(green, 16) + 0.0722 * parseInt(blue, 16);
    const lum2 = 1 - lum1;
    const contrast = Math.max(lum1, lum2);

    // Ensure color uniqueness and high contrast
    if (contrast >= threshold && !isColorDuplicate(colors, red, green, blue)) {
      colors.push(`#${red}${green}${blue}`);
    }
  }

  return colors;
}

function isColorDuplicate(existingColors: string[], red: string, green: string, blue: string) {
  const hexColor = `#${red}${green}${blue}`;
  return existingColors.includes(hexColor);
}

export function randomColor() {
  return getRandomHighContrastColors()[0];
}

export function isDate(dateToTest: string) {
  return isNaN(Number(dateToTest)) && !isNaN(Date.parse(dateToTest));
}

export function isValidNumber(stringValue: string) {
  const regEx = /^-?\d+(?:,\d+)?(?:\.\d+)?(?:[kmgt])?$/;
  return regEx.test(stringValue);
}

export function convertStringToNumber(stringValue: string | number, display = false) {
  const numberRegEx = /^[\$-]*\d+([.,\-\d$]+)[\%]*$/;
  if (typeof stringValue === 'string') {
    if (numberRegEx.test(stringValue.trim())) {
      if (parseInt(stringValue as string) === 0) {
        return 0;
      }
    }
  }
  if (isNil(stringValue)) {
    return '－';
  }
  try {
    stringValue = stringValue.toString();
  } catch (e) {
    console.log('- Value is already a string, just ignore it.')
  }

  if (typeof(stringValue) === 'string' && numberRegEx.test(stringValue.trim())) {
    // @ts-ignore
    if (stringValue.endsWith('%')) {
      if (display) {
        return numeral(stringValue).format('0.0%');
      }
    }
    return numeral(stringValue).value();

  }
  return stringValue;
}

export const isTotalKey = (key: string): boolean => {
  try {
    return key
        ? key.toLowerCase().includes('(total)')
        : false;
  } catch (e) {
    return false;
  }
}

export const isPercent = (value: string) => {
  value = value.toString();
  return value.endsWith("%");
};

export const getRGB = (color: string): { r: number; g: number; b: number } => {
  let r = 0;
  let g = 0;
  let b = 0;

  if (color.startsWith("#")) {
    // Hex format
    color = color.slice(1);
    if (color.length === 3) {
      r = parseInt(color[0] + color[0], 16);
      g = parseInt(color[1] + color[1], 16);
      b = parseInt(color[2] + color[2], 16);
    } else if (color.length === 6) {
      r = parseInt(color.slice(0, 2), 16);
      g = parseInt(color.slice(2, 4), 16);
      b = parseInt(color.slice(4, 6), 16);
    }
  } else if (color.startsWith("rgb")) {
    // RGB format
    const rgbValues = color.match(/\d+/g) as RegExpMatchArray;
    r = parseInt(rgbValues[0]);
    g = parseInt(rgbValues[1]);
    b = parseInt(rgbValues[2]);
  } else {
    // Named colors
    const ctx = document
      .createElement("canvas")
      .getContext("2d") as CanvasRenderingContext2D;
    ctx.fillStyle = color;
    const computedColor = ctx.fillStyle;
    return getRGB(computedColor);
  }

  return { r, g, b };
};

function getBrightness(r: number, g: number, b: number) {
  return (r * 299 + g * 587 + b * 114) / 1000;
}

export function getTextColor(backgroundColor: string) {
  if (!backgroundColor) {
    return 'black';
  }
  const { r, g, b } = getRGB(backgroundColor);
  const brightness = getBrightness(r, g, b);
  return brightness > 128 ? "black" : "white";
}
